/**
 * @file       Setting service for entire system
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */

import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { MULTIFACTOR_VALUE, SETTING_CODE_REF,
    DOCUMENT_VIEW_VALUE } 
    from '@app/constants/Setting.constant';
import { SETTING_API } from '@app/constants/Api.constant';
import { ERROR } from '@app/constants/Error.constant';
import { SettingInf } from '@app/secure/interface/Setting.interface';
import { Setting } from '@app/model/setting';
import { ApiService } from '@app/secure/service/Api.service';
import { AuthService } from '@app/service/auth.service';
import
{
    GetSettingsResponse,
    UpdateSettingResponse,
    PublicSsoEnabledResponse,
    PublicMaintenanceEnabledResponse
}
    from '@app/secure/interface/SettingResponses.interface';

@Injectable
({
    providedIn: 'root'
})
class SettingService 
{
    public settings$: BehaviorSubject<SettingInf[]> = 
        new BehaviorSubject<SettingInf[]>([]);
        
    /**
     * @desc - service constructor
     * @access - public
     * @param - apiService : ApiService
     * @return
     */
    constructor
    (
        private apiService: ApiService,
        private injector: Injector
    )
    {
        let storedSettings: string | null;
        let parsedSettings: {settings?: SettingInf[]; cyData?: {settings?: SettingInf[]}};

        try
        {
            storedSettings = localStorage.getItem('settings');
            if (storedSettings) 
            {
                parsedSettings = JSON.parse(storedSettings);
   
                this.settings$.next(Array.isArray(parsedSettings) ? 
                parsedSettings : parsedSettings?.settings ||
                    parsedSettings?.cyData?.settings || []);
            }
        }
        catch (error)
        {
            console.error('SettingService init localStorage:', error);
        }
    }

    /**
     * @desc   - settings array: in-memory first (localStorage may be empty
     *           or blocked), then persisted storage
     * @access - private
     * @param  -
     * @return - SettingInf[]
     */
    private getSettingsArray(): SettingInf[]
    {
        let fromMemory: SettingInf[];
        let stored: string | null;
        let parsed: {settings?: SettingInf[]; cyData?: {settings?: SettingInf[]}};
        let interim: SettingInf[];
        let result: SettingInf[];

        fromMemory = this.settings$.value;
        stored = null;
        parsed = null;
        interim = null;
        result = [];

        if (fromMemory && fromMemory.length > 0)
        {
            result = fromMemory;
        }
        else
        {
            try
            {
                stored = localStorage.getItem('settings');
                if (stored)
                {
                    parsed = JSON.parse(stored);
                    interim = Array.isArray(parsed) ?
                        parsed :
                        (parsed?.settings ||
                            parsed?.cyData?.settings ||
                            []);
                    if (Array.isArray(interim))
                    {
                        result = interim;
                    }
                }
            }
            catch (error)
            {
                console.error('getSettingsArray read error:', error);
                result = [];
            }
        }

        return result;
    }

    /**
     * @desc - set the settings
     * @access - public
     * @param - settings : Setting
     * @return -
     */
    setSettings
    (
        settings: SettingInf[]
    )
    {
        this.settings$.next(settings);
        try
        {
            if (!settings || settings.length === 0)
            {
                localStorage.removeItem('settings');
            }
            else
            {
                localStorage.setItem(
                    'settings',
                    JSON.stringify(settings)
                );
            }
        }
        catch (error)
        {
            console.error('setSettings localStorage:', error);
        }
    }

    /**
     * @desc   - clear cached settings after logout
     * @access - public
     * @param  -
     * @return -
     */
    clearPersistedStateOnLogout
    (
    )
    {
        this.settings$.next([]);
        try
        {
            localStorage.removeItem('settings');
            localStorage.removeItem('taskNumberGen');
        }
        catch (error)
        {
            console.error('clearPersistedStateOnLogout:', error);
        }
    }

    private isAuthSessionActive
    (
    ): boolean
    {
        let auth: AuthService | null;
        let result: boolean;

        auth = null;
        result = false;
        try
        {
            auth = this.injector.get(AuthService);
        }
        catch (e)
        {
            auth = null;
        }

        if (auth && auth.userValue)
        {
            result = true;
        }

        return result;
    }

    /**
     * @desc - gets the settings from the server
     * @access - public
     * @param -
     * @return - Observable<>
     */
    getSettings() 
    {
        let settings: SettingInf[];

        this.apiService.post<GetSettingsResponse>(SETTING_API.GET_SETTINGS, {})
        .subscribe(
        {
            next: (response) => 
            {
                if (!this.isAuthSessionActive())
                {
                    return;
                }

                settings =
                    (response?.cyData?.settings ||
                    response?.settings ||
                    []) as SettingInf[];
                if (Array.isArray(settings))
                {
                    this.setSettings(settings);
                }
            },
            error: (err) => 
            {
                console.error('getSettings failed:', err);
            }
        });
    }

    /**
     * @desc   - Fetch settings and return the loaded array
     * @access - public
     * @param  -
     * @return - Observable settings list
     */
    public fetchSettings(): Observable<SettingInf[]>
    {
        let result$;

        result$ = this.apiService.post<GetSettingsResponse>(SETTING_API.GET_SETTINGS, {})
            .pipe(
                map((response: GetSettingsResponse) =>
                {
                    let settings;

                    settings =
                        (response?.cyData?.settings ||
                        response?.settings ||
                        []) as SettingInf[];
                    if (!Array.isArray(settings))
                    {
                        settings = [];
                    }
                    this.setSettings(settings);

                    return settings;
                }),
                catchError((err) =>
                {
                    console.error('fetchSettings failed:', err);

                    return of(this.getSettingsArray());
                })
            );

        return result$;
    }

    /**
     * @desc - update system settings
     * @access - public
     * @param - postData - post data contains settings array
     * @return - Observable<>
     */
    public updateSettings
    (
        postData: {systemSettings: SettingInf[]}
    ) : Observable<UpdateSettingResponse>
    {
        let serviceObj: Observable<UpdateSettingResponse>;
        let cyResult: UpdateSettingResponse;

        try
        {
            serviceObj =
                    this.apiService
                    .post<UpdateSettingResponse>(SETTING_API.UPDATE_SETTINGS, postData)
            .pipe(map((data: UpdateSettingResponse) =>
            {
                cyResult =
                {
                    cyData: {},
                    errorCode: ERROR.NO_ERROR,
                    errorMessage: null
                };
                cyResult.cyData = data;

                return cyResult;
            }),
            catchError((err: Error) => 
            {
                let errorParts: string[];
                let errMsg: string;

                errMsg = err.message;
                errorParts = typeof errMsg === 'string' ? errMsg.split(',') : [];
                cyResult = 
                { 
                    updated: false,
                    errorCode: null,
                    errorMessage: null 
                };

                if (errorParts && errorParts.length > 1 && errorParts[0])
                {
                    switch (Number(errorParts[1]))
                    {
                        case ERROR.GENERAL_DB:
                            cyResult.errorCode = errorParts[1];
                            cyResult.errorMessage = 'General DB Error';
                            break;
                        default:
                            cyResult.errorCode = errorParts[1];
                            cyResult.errorMessage = 'General Error';
                            break;
                    }
                }

                return of(cyResult);
            }));
        }
        catch (e)
        {
            console.log(e);
            cyResult =
            {
                errorCode: ERROR.GENERAL,
                errorMessage: 'General Error'
            };

            return of(cyResult);
        }

        return serviceObj;
    }

    public getPublicSsoEnabled(): Observable<boolean>
    {
        let serviceObj: Observable<boolean>;

        serviceObj = this.apiService
            .get<PublicSsoEnabledResponse>(SETTING_API.GET_PUBLIC_SSO_ENABLED)
        .pipe(
            map((response: PublicSsoEnabledResponse) =>
            {
                let enabled: boolean;

                enabled = false;
                enabled = response?.ssoEnabled === true;

                return enabled;
            }),
            catchError(() =>
            {
                return of(false);
            })
        );

        return serviceObj;
    }

    /**
     * @desc   - public maintenance mode for login gate
     * @access - public
     * @param  -
     * @return - Observable of maintenanceEnabled
     */
    public getPublicMaintenanceEnabled(): Observable<boolean>
    {
        let serviceObj: Observable<boolean>;

        serviceObj = this.apiService
            .get<PublicMaintenanceEnabledResponse>(
                SETTING_API.GET_PUBLIC_MAINTENANCE_ENABLED
            )
        .pipe(
            map((response: PublicMaintenanceEnabledResponse) =>
            {
                let enabled: boolean;

                enabled = false;
                enabled =
                    response?.maintenanceEnabled === true;

                return enabled;
            }),
            catchError(() =>
            {
                return of(false);
            })
        );

        return serviceObj;
    }
    
    /**
     * @desc - sets the task number generation
     * @access - public
     * @param - value : string
     * @return
     */
    public setTaskNumberGen
    (
        value
    )
    {
        localStorage.setItem('taskNumberGen', value);
    }
    
    /**
     * @desc - gets the task number generation
     * @access - public
     * @param -
     * @return - object : taskNumberGen
     */
    public getTaskNumberGen()
    {
        let taskNumberGen;
        
        taskNumberGen = localStorage.getItem('taskNumberGen') ? 
                        localStorage.getItem('taskNumberGen') : null;
        
//        if (taskNumberGen)
//        {
//            this.taskNumberGen$.next(taskNumberGen)
//        }
        
        return taskNumberGen;
    }
    
    /**
     * @desc - checks if the task number generation is auto
     * @access - public
     * @param -
     * @return - boolean : taskNumberAutoGen
     */
    public isTaskNumberAutoGen()
    {
        let taskNumberAutoGen : boolean;
        let taskNumberGen : SettingInf | null;
    
        // initialize variables
        taskNumberAutoGen = false;        
        taskNumberGen = this.getTaskNumberGen();
        
        if (taskNumberGen)
        {
            taskNumberAutoGen = Setting.isTaskNumberAutoGen(taskNumberGen)    
        }
        
        return taskNumberAutoGen;
    }

    /**
     * @desc   - load system settings from the server
     * @access - public
     * @param  - boolean forceReload : reload even when cached
     * @return - Promise<void>
     */
    public async loadSettings
    (
        forceReload: boolean = false
    )
    {
        let data: Record<string, never>;
        let response: GetSettingsResponse;
        let shouldReload: boolean;
        let settings: SettingInf[];
        let taskNumberSetting: SettingInf | undefined;

        try
        {
            shouldReload = false;
            settings = [];

            shouldReload =
                forceReload ||
                !this.settings$.value ||
                this.settings$.value.length === 0;

            if (shouldReload)
            {
                data = {};
                response = await this.apiService.post<GetSettingsResponse>(
                    SETTING_API.GET_SETTINGS,
                    data
                ).toPromise();

                let interim: SettingInf[] | undefined;
                
                interim = (response?.cyData?.settings ||
                    response?.settings) as SettingInf[] | undefined;
                
                settings = Array.isArray(interim) ? interim : [];
                
                if (settings.length > 0)
                {
                    this.setSettings(settings);
                    let found: SettingInf | boolean;
                    
                    found = Setting.getTaskNumberCode(settings);
                    if (found && typeof found !== 'boolean')
                    {
                        taskNumberSetting = found;
                    }
                }
                
                if (taskNumberSetting)
                {
                    this.setTaskNumberGen(
                        taskNumberSetting.value
                    );
                }
            }
        }
        catch (error)
        {
            console.error('loadSettings failed:', error);
        }
    }

    /**
     * @desc   - true when global MFA is email, SMS, or user choice
     * @access - public
     * @param  -
     * @return - boolean : multiFactorEnabled
     */
    public isMultiFactorEnabled(): boolean 
    {
        let multiFactorEnabled: boolean;
        let settings: SettingInf[];
        let multiFactorSetting: SettingInf | undefined;
        let settingValue: string;

        multiFactorEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            multiFactorSetting = settings.find((item: SettingInf) =>
                Number(item.code) ===
                    SETTING_CODE_REF.MULTIFACTOR_VERIFICATION_CODE
            );

            if (multiFactorSetting)
            {
                settingValue = String(multiFactorSetting.value);
                multiFactorEnabled =
                    settingValue ===
                        MULTIFACTOR_VALUE.EMAIL_VERIFICATION ||
                    settingValue ===
                        MULTIFACTOR_VALUE.SMS_VERIFICATION ||
                    settingValue ===
                        MULTIFACTOR_VALUE.EMAIL_AND_SMS_VERIFICATION;
            }
        }

        return multiFactorEnabled;
    }

    /**
     * @desc - checks if the Jury System is enabled
     * @access - public
     * @param -
     * @return - boolean : jurySystemEnabled
     */
    public isJurySystemEnabled(): boolean 
    {
        let jurySystemEnabled: boolean;
        let settings: SettingInf[];
        let jurySetting: SettingInf | undefined;

        jurySystemEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            jurySetting = settings.find((item: SettingInf) =>
                Number(item.code) ===
                    SETTING_CODE_REF.JURY_SYSTEM_CODE &&
                item.value === "1"
            );
            jurySystemEnabled = !!jurySetting;
        }

        return jurySystemEnabled;
    }

    /**
     * @desc   - checks if the Court Management System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : courtSystemEnabled
     */
    public isCourtSystemEnabled(): boolean
    {
        let courtSystemEnabled: boolean;
        let settings: SettingInf[];
        let courtSetting: SettingInf | undefined;

        courtSystemEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            courtSetting =
                settings.find(
                    (item: SettingInf) =>
                        Number(item.code) ===
                        SETTING_CODE_REF.COURT_SYSTEM_CODE
                        && item.value === "1"
                );
            courtSystemEnabled = !!courtSetting;
        }

        return courtSystemEnabled;
    }

    /**
     * @desc   - checks if Case Queue is enabled
     * @access - public
     * @param  -
     * @return - boolean : caseQueueEnabled
     */
    public isCaseQueueEnabled(): boolean
    {
        let caseQueueEnabled: boolean;
        let settings: SettingInf[];
        let caseQueueSetting: SettingInf | undefined;

        caseQueueEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            caseQueueSetting =
                settings.find(
                    (item: SettingInf) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .CASE_QUEUE_MODULE_CODE
                        && item.value === "1"
                );
            caseQueueEnabled =
                !!caseQueueSetting;
        }

        return caseQueueEnabled;
    }

    /**
     * @desc   - checks if the Docket System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : docketEnabled
     */
    public isDocketSystemEnabled(): boolean
    {
        let docketEnabled: boolean;
        let settings: SettingInf[];
        let docketSetting: SettingInf | undefined;

        docketEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            docketSetting =
                settings.find(
                    (item: SettingInf) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .DOCKET_SYSTEM_CODE
                        && item.value === "1"
                );
            docketEnabled =
                !!docketSetting;
        }

        return docketEnabled;
    }

    /**
     * @desc   - checks if Calendar (court calendar) is enabled
     * @access - public
     * @param  -
     * @return - boolean : courtCalendarEnabled
     */
    public isCourtCalendarEnabled(): boolean
    {
        let courtCalendarEnabled: boolean;
        let settings: SettingInf[];
        let courtCalendarSetting: SettingInf | undefined;

        courtCalendarEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            courtCalendarSetting =
                settings.find(
                    (item: SettingInf) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .COURT_CALENDAR_SYSTEM_CODE
                        && item.value === "1"
                );
            courtCalendarEnabled =
                !!courtCalendarSetting;
        }

        return courtCalendarEnabled;
    }

    /**
     * @desc   - checks if the Audit System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : auditEnabled
     */
    public isAuditSystemEnabled(): boolean
    {
        let auditEnabled: boolean;
        let settings: SettingInf[];
        let auditSetting: SettingInf | undefined;

        auditEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            auditSetting =
                settings.find(
                    (item: SettingInf) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .CASE_AUDIT_SYSTEM_CODE
                        && item.value === "1"
                );
            auditEnabled =
                !!auditSetting;
        }

        return auditEnabled;
    }

    /**
     * @desc   - checks if the Probation System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : probationEnabled
     */
    public isProbationSystemEnabled(): boolean
    {
        let probationEnabled: boolean;
        let settings: SettingInf[];
        let probationSetting: SettingInf | undefined;

        probationEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            probationSetting =
                settings.find(
                    (item: SettingInf) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .PROBATION_SYSTEM_CODE
                        && item.value === "1"
                );
            probationEnabled =
                !!probationSetting;
        }

        return probationEnabled;
    }

    /**
     * @desc   - checks if the Bond System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : bondEnabled
     */
    public isBondSystemEnabled(): boolean
    {
        let bondEnabled: boolean;
        let settings: SettingInf[];
        let bondSetting: SettingInf | undefined;

        bondEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            bondSetting =
                settings.find(
                    (item: SettingInf) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .BOND_SYSTEM_CODE
                        && item.value === "1"
                );
            bondEnabled =
                !!bondSetting;
        }

        return bondEnabled;
    }

    /**
     * @desc   - true when the Bond Agency setting (52) is on, so
     *           Bond Providers shows a separate Agency tab
     * @access - public
     * @param  -
     * @return - boolean
     */
    public isBondAgencyEnabled(): boolean
    {
        let agencyEnabled: boolean;
        let settings: SettingInf[];
        let agencySetting: SettingInf | undefined;

        agencyEnabled = false;
        settings = this.getSettingsArray();
        agencySetting = settings.find(
            (item: SettingInf) =>
                Number(item.code) ===
                    SETTING_CODE_REF.BOND_AGENCY_CODE &&
                item.value === '1'
        );
        agencyEnabled = !!agencySetting;

        return agencyEnabled;
    }

    /**
     * @desc   - returns the list of supported form template
     *           image keys from the system setting
     * @access - public
     * @param  -
     * @return - string[] : image key names
     */
    public getFormTemplateImageKeys(): string[]
    {
        let settings: SettingInf[];
        let imageSetting: SettingInf | undefined;
        let parsed: SettingInf[];
        let result: string[];

        result = [];
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            imageSetting = settings.find(
                (item: SettingInf) =>
                    Number(item.code) ===
                    SETTING_CODE_REF
                        .FORM_TEMPLATE_IMAGES_CODE
            );
            if (imageSetting && imageSetting.value)
            {
                try
                {
                    let parsedValue: string[];
                    
                    parsedValue = JSON.parse(
                        imageSetting.value as string
                    );
                    if (Array.isArray(parsedValue))
                    {
                        result = parsedValue;
                    }
                }
                catch (e)
                {
                    console.error(
                        'getFormTemplateImageKeys parse:',
                        e
                    );
                }
            }
        }

        return result;
    }

    /**
     * @desc   - document screen view mode from system setting
     * @access - public
     * @param  -
     * @return - string : avatar or list
     */
    public getDocumentViewMode(): string
    {
        let settings: SettingInf[];
        let viewSetting: SettingInf | undefined;
        let value: string;
        let result: string;

        result = DOCUMENT_VIEW_VALUE.AVATAR;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            viewSetting = settings.find(
                (item: SettingInf) =>
                    Number(item.code) ===
                    SETTING_CODE_REF.DOCUMENT_VIEW_MODE_CODE
            );
            value = viewSetting?.value ?
                String(viewSetting.value).trim().toLowerCase() :
                '';
            if (value === DOCUMENT_VIEW_VALUE.LIST)
            {
                result = DOCUMENT_VIEW_VALUE.LIST;
            }
        }

        return result;
    }

    /**
     * @desc   - payment plan SMS reminder days from setting
     * @access - public
     * @param  -
     * @return - number : days before due date
     */
    public getPaymentPlanTextMessageDays(): number
    {
        let settings: SettingInf[];
        let daysSetting: SettingInf | undefined;
        let parsed: number;
        let result: number;

        result = 3;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            daysSetting = settings.find(
                (item: SettingInf) =>
                    Number(item.code) ===
                    SETTING_CODE_REF
                        .PAYMENT_PLAN_TEXT_MESSAGE_DAYS_CODE
            );
            if (daysSetting && daysSetting.value != null)
            {
                parsed = Number(daysSetting.value);
                if (!isNaN(parsed) && parsed >= 0)
                {
                    result = parsed;
                }
            }
        }

        return result;
    }

    /**
     * @desc   - Raw JSON value for case-form PDF margins (setting 40)
     * @access - public
     * @param  -
     * @return - string JSON or empty when unset
     */
    public getCaseFormPdfMarginJson(): string
    {
        let settings: SettingInf[];
        let marginSetting: SettingInf | undefined;
        let result: string;

        result = '';
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            marginSetting = settings.find(
                (item: SettingInf) =>
                    Number(item.code) ===
                    SETTING_CODE_REF.CASE_FORM_PDF_MARGIN_CODE
            );
            if (marginSetting && marginSetting.value != null)
            {
                result = String(marginSetting.value);
            }
        }

        return result;
    }

    /**
     * @desc   - Read form editor mode (html | msWord) and lock state
     * @access - public
     * @param  -
     * @return - Observable API response
     */
    public getFormEditorMode
    (
    )
    {
        let result;

        result = this.apiService.post(
            SETTING_API.GET_FORM_EDITOR_MODE,
            {}
        );

        return result;
    }

    /**
     * @desc   - Update form editor mode when unlocked
     * @access - public
     * @param  - mode : html or msWord
     * @return - Observable API response
     */
    public updateFormEditorMode
    (
        mode: string
    )
    {
        let result;

        result = this.apiService.post(
            SETTING_API.UPDATE_FORM_EDITOR_MODE,
            { mode: mode }
        );

        return result;
    }
}

export { SettingService };
